import React, { useEffect } from "react";
import styles from "./FinancialCheckPage.scss";
import cx from "../../utils/cx";
import LeadSmartApi, { ILeadConsumer, ILeadData, ILeadResponse, IMortageData, IMortageResponse } from "../../utils/LeadSmartApi";
import PropertyRequest from "../../utils/PropertyRequest";
import IHome from "../../interfaces/IHome";
import LoanData from "./LoanData";
import { IProperty } from "../../utils/JsonApiRequest";
import LoadingIndicator from "../LoadingIndicator";
import FirstPersonData from "./FirstPersonData";
import Result from "./Result";
import searchApi from "../../utils/searchApi";
import Affiliate, { IAffiliateDetails } from "../../utils/Affiliate";
import ThankYou from "./ThankYou";
import { pathFinanzierungsbestaetigung } from "../RegisterSidePanel";
import trackApi from "../../utils/trackApi";
import { typeOfEmploymentList } from "../RegisterInvitedUser/UserDetailsOptions";

/**
 * WGLDE-2030 - Flow for Genokon 2023 mit Beispielbank VB Köln-Bonn und speziell definierter Test-Immobilie
 */


// TODO: remove
const initTestData = {
    // "affiliate": {
    //   "name": "vb-gestalterbank",
    //   "display_name": "Volksbank eG - Gestalterbank",
    //   "logo_url": "/sites/dnz/files/2022-12/Logo_Voba_Gestalterbank_1.jpg",
    //   "logo_filemime": "image/jpeg",
    //   "pia_prod": "https://pia.gestalterbank.de",
    //   "pia_preview": "https://vb-gestalterbank.preview.impleco.de",
    //   "privacy_uri": "https://www.volksbank-koeln-bonn.de/service/rechtliche-hinweise/datenschutzhinweis-zur-website.html"
    // },

    // "loanData": {
    //   "price": 165000,
    //   "closingCosts": 11550,
    //   "equity": 100000,
    //   "loan": 76550,
    //   "remainingDebt": 0
    // },

    // "firstPerson": {
    //   "firstName": "Falk",
    //   "lastName": "Brückner",
    //   "email": "fabr@gmx.de",
    //   "birthday": "1999-01-01",
    //   "employedSince": "2020-01-01",
    //   "monthlyNetIncomeInEuro": 5000
    // },
    // "termsData": {
    //   "homecloud": true,
    //   "email": false
    // }
}


const steps = [
    {
        id: '',
        component: LoanData,
    },
    {
        id: 'firstPersonData',
        component: FirstPersonData,
    },
    {
        id: 'result',
        component: Result,
    },
    {
        id: 'thankYou', // only when terms.email was false in firstPersonData
        component: ThankYou,
    }
]


/**
 * 
 * virtual Tracking depending on various user decisions, see WGLDE-2751
 * 
 */
let lastVirtPath = '';
const tracking = (step: typeof steps[0], hasCheckedEmail: boolean | undefined) => {

    let virtTitle = '';
    let virtPath = '';
    let additionalProps = {
        'virtLocation': ``,
        'virtReferrer': '',
        'streckenzweig1': ''
    }

    const streckenzweig1 = hasCheckedEmail === true
        ? 'Beratung sofort ausgewaehlt'
        : hasCheckedEmail === false
            ? 'Beratung später ausgewaehlt'
            : 'nicht befuellbar';

    switch (step.id) {
        case '':
            virtTitle = 'Finanzierungscheck Start';
            virtPath = '/service/finanzierungscheck/start';
            additionalProps = {
                'virtLocation': `${location.origin}${virtPath}`,
                'virtReferrer': lastVirtPath ? lastVirtPath : document.referrer,
                'streckenzweig1': streckenzweig1
            }
            break;
        case 'firstPersonData':
            virtTitle = 'Finanzierungscheck Persoenliche Angaben';
            virtPath = `/service/finanzierungscheck/persoenliche-angaben`;
            additionalProps = {
                'virtLocation': `${location.origin}${virtPath}`,
                'virtReferrer': lastVirtPath,
                'streckenzweig1': streckenzweig1
            }
            break;
        case 'result':
            virtTitle = hasCheckedEmail ? 'Finanzierungscheck Ende' : 'Finanzierungscheck Bank kontaktieren';
            virtPath = hasCheckedEmail ? `/service/finanzierungscheck/ende` : `/service/finanzierungscheck/bank-kontaktieren`;
            additionalProps = {
                'virtLocation': `${location.origin}${virtPath}`,
                'virtReferrer': lastVirtPath,
                'streckenzweig1': streckenzweig1
            }
            break;
        case 'thankYou':
            virtTitle = 'Finanzierungscheck Ende';
            virtPath = `/service/finanzierungscheck/ende`;
            additionalProps = {
                'virtLocation': `${location.origin}${virtPath}`,
                'virtReferrer': lastVirtPath,
                'streckenzweig1': 'Beratung später ausgewaehlt'
            }
            break;
    }

    lastVirtPath = virtPath;
    trackApi.wglPageview(virtPath, virtTitle, additionalProps);
}


export interface IFinancialCheckPageProps {
  className?: string;
  uuid?: string;
}

export type IFinancialCheckData = Partial<{
  property: IProperty;
  loanData: {
    price: number,
    closingCosts: number,
    equity: number,
    loan: number, // Preis + Nebenkosten - Eigenkapital
    remainingDebt: number
  };
  termsData: { homecloud: boolean, email: boolean };
  firstPerson: Omit<ILeadData['firstPerson'], 'employmentType'> & {postalCode: string, typeOfEmployment: keyof typeof typeOfEmploymentList} & IMortageData['firstPerson'];
  mortgageResponse: IMortageResponse;
  leadResponse: ILeadResponse;
  campaign: ILeadConsumer;
  affiliate: IAffiliateDetails;
}>;

export type INextStepData = { [key in keyof IFinancialCheckData]: IFinancialCheckData[key] };

const setHash = (hash: string) => {
    if (hash) {
        location.hash = hash;
    } else {
    // clear hash and fire hashchange event
        history.pushState("", document.title, window.location.pathname + window.location.search);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
};

const FinancialCheckPage: React.FC<IFinancialCheckPageProps> = ({ className = '', uuid }) => {

    const [activeStep, setActiveStep] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(true);
    // const [homeData, setHomeData] = React.useState<IProperty>(null);
    const [data, setData] = React.useState<IFinancialCheckData>((initTestData as any) || {});

    useEffect(() => {

        /**
     * Haus Daten holen
     */
        if (uuid) {
            PropertyRequest.getProperty(uuid).then((property: IProperty) => {
                if (!property.property_id) {
                    console.warn('Finanzierungscheck: keine external home.property_id vorhanden');
                }

                // TODO remove: Fallback for dev
                if (property.drupal_internal__id === 101798) {
                    property.property_id = 'FIO-1234567890';
                }
                // TODO remove: Fallback for dev stage
                if (property.property_id === 'wgl-168078') {
                    property.property_id = '2698';
                }
                // TODO remove: Fallback for dev stage
                if (property.property_id === '0') {
                    property.property_id = '2698';
                }
                // TODO remove: Fallback for dev stage
                if (property.uid === 462) {
                    property.property_id = '2698';
                }

                console.debug('Finanzierungscheck: home', property);

                /**
         * testen, ob die externalID bei LeadSmart bekannt ist
         */
                LeadSmartApi.getHomeByExternalId(property.property_id).then((homeResponse) => {
                    setData(prev => ({ ...prev, home: property }));
                }).catch(() => {
                    // redirect to other flow
                    location.replace(pathFinanzierungsbestaetigung);
                }).finally(() => {
                    setIsLoading(false);
                });

            }).catch((e) => {
                console.warn('Finanzierungscheck: ExternalId nicht bekannt', e);
                console.error(e);
                setIsLoading(false);
                // redirect to other flow
                location.replace(pathFinanzierungsbestaetigung);
            });

            searchApi.getPropertyByUUID(uuid).then((home) => {
                Affiliate.getDetails(home.affiliates[0]).then((details) => {
                    setData(prev => ({ ...prev, affiliate: details }));
                });
            });
        } else {
            console.warn('Finanzierungscheck: keine UUID vorhanden');
            setIsLoading(false);
        }

        /**
     * Hash-Change Listener / steps routing
     */
        const updateHash = () => {
            const hash = decodeURIComponent(location.hash.replace('#', ''));
            const step = steps.findIndex(({ id }) => id === hash);
            if (step !== -1) {
                setActiveStep(step);
            } else {
                setActiveStep(0);
            }
        };

        // delete hash on initial load
        history.pushState("", document.title, window.location.pathname + window.location.search);
        addEventListener("hashchange", updateHash);

        return () => {
            removeEventListener("hashchange", updateHash);
        };

    }, []);

    useEffect(() => {
        tracking(steps[activeStep], data.termsData ? !!data.termsData.email : undefined);
    }, [activeStep]);

    const nextStep = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const nextStep = steps[activeStep + 1]?.id || '';
        setHash(nextStep);
    }

    const handleSetData = (data?: INextStepData) => {
        setData(prev => ({ ...prev, ...data }));
    }

    const prevStep = (data?: { [key in keyof IFinancialCheckData]: IFinancialCheckData[key] }) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setData(prev => ({ ...prev, ...data }));
        const prevStep = steps[activeStep - 1]?.id || '';
        setHash(prevStep);
    };


    const StepComponent = steps[activeStep]?.component || console.error('no step component for step', activeStep) || steps[0].component;


    return <div className={cx(styles.financialCheckPage, className)}>
        <div className={styles.paper}>

            {isLoading
                ?
                    <LoadingIndicator />
                :
                data.property ? <StepComponent data={data} nextStep={nextStep} setData={handleSetData} prevStep={prevStep} /> : <><b>Finanzierungscheck:</b> keine (noch gültige) ID vorhanden!</>
            }
        </div>

    </div>
};

export default FinancialCheckPage;
